import {EmployeeRef, KeyData, NewJoiner, OfficeVisitSheet} from "./Model";
import {create} from "zustand";
import {useVisitReportStore} from "./VisitReportStore";
import {useKeyDataStore} from "./KeyDataStore";
import {useNewJoinerStore} from "./NewJoinerStore";
import {RemoteValue} from "../util/RemoteValue";
import {derivedRemoteStore} from "./DerivedStore";
import {associateBy, getOrSet} from "../util/MapUtil";
import {combineCmp, numberCmp, stringCmp} from "../util/SortUtil";
import {mapNotNull} from "../util/ArrayUtil";
import {isWeekend} from "../util/DateUtil";
import {useEmployeeMapStore, EmployeeMap} from "./EmployeeMapStore";

export enum EmployeeVisitType {
    BeforeStart = 'before-start',
    Absent = 'absent',
    WorkDayVisit = 'work-day-visit',
    WeekendVisit = 'weekend-visit',
}

export type EmployeeVisitSheet = OfficeVisitSheet<EmployeeRef, EmployeeVisitType>;

export function isEmployeeVisit(type: EmployeeVisitType): boolean {
    return type === EmployeeVisitType.WorkDayVisit || type === EmployeeVisitType.WeekendVisit;
}

export function employeeVisitType(
    date: string,
    visited: boolean,
    employeeStartDate?: string | null,
): EmployeeVisitType {
    if (employeeStartDate && date < employeeStartDate) {
        return EmployeeVisitType.BeforeStart;
    }
    if (!visited) {
        return EmployeeVisitType.Absent;
    }
    return isWeekend(date) ? EmployeeVisitType.WeekendVisit : EmployeeVisitType.WorkDayVisit;
}

export const useEmployeeVisitStore = create<{ remoteValue: RemoteValue<EmployeeVisitSheet> }>()(
    derivedRemoteStore(
        {
            keyData: useKeyDataStore,
            visitReports: useVisitReportStore,
            employeeMap: useEmployeeMapStore,
            newJoiners: useNewJoinerStore,
        },
        ({keyData, visitReports, employeeMap, newJoiners}) => {
            const sheets = visitReports.map(report => report.sheet);
            return calculateEmployeeVisitSheet(keyData, sheets, employeeMap, newJoiners);
        },
    )
);

export function calculateEmployeeVisitSheet(
    keyData: KeyData[],
    keyVisitSheets: OfficeVisitSheet<KeyData['keyNumber']>[],
    employeeMap: EmployeeMap,
    newJoiners: NewJoiner[],
): EmployeeVisitSheet {
    // Build quick lookup from key number to KeyData
    const keyByNumber = associateBy(keyData, k => k.keyNumber);

    const employeeId2newJoinerDate = new Map<EmployeeRef['id'], NewJoiner['joinDate']>(
        mapNotNull(
            newJoiners,
            newJoiner => newJoiner.person.type === 'existing' ? [
                newJoiner.person.employee.id,
                newJoiner.joinDate,
            ] : null
        ),
    );

    // Collect union of all dates across provided sheets
    const dateSet = new Set<string>();
    for (const sheet of keyVisitSheets) {
        sheet.dates.forEach(d => dateSet.add(d));
    }

    // Map employeeId -> { employee: EmployeeRef, dates: Set<string> }
    const employeeVisits = new Map<EmployeeRef['id'], { employee: EmployeeRef, dates: Set<string> }>();

    // Iterate through all sheets and accumulate visit dates per employee
    for (const sheet of keyVisitSheets) {
        sheet.rows.forEach((row) => {
            const kd = keyByNumber.get(row.value);
            if (!kd) return; // skip unknown keys

            // Gather dates when this key visited (true flags)
            row.visits.forEach((visited, idx) => {
                if (!visited) return;

                const date = sheet.dates[idx];
                if (!date) return;

                const keyRecordsOnDate = kd.history.filter(
                    record =>
                        (!record.from || record.from <= date) && (!record.to || date <= record.to)
                );

                const keyOwnersOnDate = mapNotNull(
                    keyRecordsOnDate,
                    (record =>
                            record.location.type === 'with-employee' ? record.location.employee : null
                    ),
                );

                keyOwnersOnDate.forEach(employee =>
                    getOrSet(
                        employeeVisits,
                        employee.id,
                        () => ({
                            employee: employee,
                            dates: new Set<string>(),
                        }),
                    ).dates.add(date)
                );
            });
        });
    }

    // Sort employees by descending number of visit dates, then by fullName
    const employeesSorted = Array.from(employeeVisits.values())
        .sort(combineCmp(
            numberCmp(it => it.dates.size, true),
            stringCmp(it => it.employee.fullName),
        ))
        .map(e => e.employee);


    const allDates = Array.from(dateSet.values())
        .sort(stringCmp((it: string) => it));
    // Build rows for the resulting sheet
    const rows = employeesSorted.map(emp => {
        const visitsSet = employeeVisits.get(emp.id)?.dates ?? new Set<string>();
        const startDate = employeeId2newJoinerDate.get(emp.id) ?? employeeMap.get(emp)?.startDate;
        const visits = allDates.map(date => employeeVisitType(date, visitsSet.has(date), startDate));
        return {value: emp, visits};
    });

    return {dates: allDates, rows, length: rows.length};
}
